import { Navigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import PitBoardLogo from "@/components/PitBoardLogo";
import PacksCarousel from "@/components/PacksCarousel";
import { CheckCircle2, Calculator, FileText, TrendingUp, AlertCircle, Mail, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

const FEATURES = [
  { icon: Calculator, title: "Calcul de coût horaire", desc: "Taux de facturation minimum auto-calculé en temps réel" },
  { icon: FileText, title: "Devis & factures PDF", desc: "Base de prix peinture, RIB + CGV intégrés, prêts à envoyer" },
  { icon: TrendingUp, title: "Trésorerie en direct", desc: "Cockpit financier, projections, IS prévisionnel" },
];

const ERROR_MESSAGES = {
  auth_failed: "L'échange du jeton de connexion a échoué. Recommencez la connexion Google.",
  missing_session_id: "Le retour Google n'a pas fourni de session. Réessayez.",
  session_expired: "Votre session a expiré. Reconnectez-vous.",
  session_already_used: "Le lien Google précédent a déjà été utilisé (valable une seule fois, ~30s). Cliquez sur 'Connexion avec Google' ci-dessous pour une nouvelle tentative.",
};

export default function Login() {
  const { user, loading } = useAuth();
  const [magicEmail, setMagicEmail] = useState("");
  const [magicSending, setMagicSending] = useState(false);
  const [magicSent, setMagicSent] = useState(false);
  const [magicError, setMagicError] = useState(null);

  if (loading) return null;
  if (user) return <Navigate to="/" replace />;

  // Récupère ?error= de l'URL pour afficher un message clair
  const params = new URLSearchParams(window.location.search);
  const errorCode = params.get("error");
  const errorMsg = errorCode ? (ERROR_MESSAGES[errorCode] || `Erreur : ${errorCode}`) : null;

  const handleGoogleLogin = () => {
    // Nettoyage défensif : on supprime toute trace résiduelle avant de relancer l'OAuth
    try { localStorage.removeItem("pitboard_session_token"); } catch { /* noop */ }
    try { document.cookie = "pb_tk=; path=/; expires=Thu, 01 Jan 1970 00:00:01 GMT"; } catch { /* noop */ }
    try { sessionStorage.removeItem("pitboard_consumed_session_id"); } catch { /* noop */ }
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleMagicLink = async (e) => {
    e.preventDefault();
    setMagicError(null);
    const email = magicEmail.trim().toLowerCase();
    if (!email || !email.includes("@")) {
      setMagicError("Email invalide");
      return;
    }
    setMagicSending(true);
    try {
      await api.post("/auth/magic-link-request", { email });
      setMagicSent(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setMagicError(typeof detail === "string" ? detail : "Erreur d'envoi du lien. Réessayez.");
    } finally {
      setMagicSending(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-app text-[var(--chalk)]" data-testid="login-page">
      {/* Left — texture carbone signature */}
      <div className="hidden lg:flex w-1/2 relative carbon-texture">
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(11,11,12,0.65) 0%, rgba(11,11,12,0.85) 100%)" }} />
        {/* Yellow accent stripe top */}
        <div className="absolute top-0 left-0 right-0 h-1" style={{ background: "var(--pit-yellow)" }} />
        <div className="absolute bottom-0 left-0 right-0 h-1" style={{ background: "var(--board-red)" }} />

        <div className="relative h-full w-full flex flex-col justify-between p-14">
          <PitBoardLogo size={72} />

          <div className="max-w-xl">
            <p className="flex items-center gap-1 font-body text-xs font-semibold mb-7" style={{ color: "var(--pit-yellow)" }}>
              <span className="ornament" style={{ background: "var(--pit-yellow)" }} />
              Pilotez enfin votre entreprise
            </p>
            <h1 className="font-display text-[3.2rem] xl:text-6xl leading-[0.95] tracking-tighter mb-8">
              VOTRE BUSINESS<br/>
              À <span style={{ color: "var(--pit-yellow)" }}>360°.</span><br/>
              ZÉRO <span style={{ color: "var(--board-red)" }}>ANGLE MORT.</span>
            </h1>
            <p className="text-[16px] leading-relaxed font-body max-w-md" style={{ color: "var(--ink-soft)" }}>
              Devis, factures PDF, coûts horaires, trésorerie et impôt prévisionnel.
              Toute votre gestion dans un seul cockpit, pensé pour le terrain.
            </p>
          </div>

          <div className="space-y-4 max-w-md">
            {FEATURES.map(({ icon: Icon, title, desc }) => (
              <div key={title} className="flex items-start gap-4">
                <div className="w-10 h-10 rounded flex items-center justify-center shrink-0 border" style={{ background: "var(--carbon-2)", borderColor: "var(--steel)" }}>
                  <Icon size={16} style={{ color: "var(--pit-yellow)" }} strokeWidth={2} />
                </div>
                <div>
                  <div className="font-display text-base" style={{ fontWeight: 900 }}>{title}</div>
                  <div className="text-[13px] mt-0.5 font-body" style={{ color: "var(--grey)" }}>{desc}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Carousel des Packs Métiers (vitrine publique) */}
          <div className="max-w-md">
            <PacksCarousel />
          </div>
        </div>
      </div>

      {/* Right — login */}
      <div className="flex-1 flex flex-col justify-between p-6 sm:p-10 lg:p-16" style={{ background: "var(--carbon-2)" }}>
        <div className="lg:hidden mb-8">
          <PitBoardLogo size={52} />
        </div>

        <div className="flex-1 flex items-center">
          <div className="w-full max-w-md mx-auto">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-sm mb-9 border" style={{ borderColor: "var(--steel)", background: "var(--carbon)" }}>
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: "var(--pit-yellow)" }} />
              <span className="text-[11px] font-semibold" style={{ color: "var(--pit-yellow)" }}>Beta — Phase pilote</span>
            </div>

            <h2 className="font-display text-[2.8rem] mb-3 tracking-tight" style={{ color: "var(--chalk)" }}>BIENVENUE.</h2>
            <p className="font-body text-base sm:text-lg mb-7 leading-snug" style={{ color: "var(--chalk)" }}>
              <span style={{ color: "var(--pit-yellow)", fontWeight: 600 }}>Votre business à 360°.</span>{" "}
              <span style={{ color: "var(--board-red)", fontWeight: 600 }}>Zéro angle mort.</span>
            </p>
            <p className="mb-10 text-[15px] leading-relaxed font-body" style={{ color: "var(--ink-soft)" }}>
              Connectez-vous avec votre compte Google pour accéder à votre espace PitBoard. Une entreprise = un compte.
            </p>

            {errorMsg && (
              <div className="mb-6 rounded-lg p-3 flex items-start gap-3" style={{ background: "rgba(225,6,0,0.10)", border: "1px solid rgba(225,6,0,0.35)" }} data-testid="login-error">
                <AlertCircle size={18} style={{ color: "#E10600" }} className="shrink-0 mt-0.5" />
                <div className="text-[13px]" style={{ color: "var(--chalk)" }}>{errorMsg}</div>
              </div>
            )}

            <button
              onClick={handleGoogleLogin}
              className="btn btn-primary w-full mb-4"
              data-testid="google-login-btn"
              style={{ minHeight: 54, fontFamily: "'Inter', sans-serif", letterSpacing: 0, textTransform: "none", fontSize: "0.95rem", fontWeight: 600 }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" className="-ml-1">
                <path fill="currentColor" d="M21.35 11.1h-9.18v2.92h5.27c-.23 1.42-1.7 4.17-5.27 4.17-3.17 0-5.76-2.62-5.76-5.85s2.59-5.85 5.76-5.85c1.81 0 3.02.77 3.71 1.43l2.53-2.44C16.94 4.04 14.83 3 12.17 3 6.94 3 2.83 7.18 2.83 12.34s4.11 9.34 9.34 9.34c5.39 0 8.97-3.79 8.97-9.13 0-.61-.07-1.08-.16-1.55z"/>
              </svg>
              Connexion avec Google
            </button>

            {/* Séparateur "ou" */}
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-1 h-px" style={{ background: "var(--steel)" }} />
              <span className="text-[11px] font-medium uppercase tracking-wider" style={{ color: "var(--grey)" }}>OU</span>
              <div className="flex-1 h-px" style={{ background: "var(--steel)" }} />
            </div>

            {/* Magic Link form */}
            {!magicSent ? (
              <form onSubmit={handleMagicLink} className="mb-6" data-testid="magic-link-form">
                <div className="relative mb-2">
                  <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--grey)" }} />
                  <input
                    type="email"
                    value={magicEmail}
                    onChange={(e) => { setMagicEmail(e.target.value); setMagicError(null); }}
                    placeholder="votre.email@entreprise.fr"
                    disabled={magicSending}
                    className="w-full pl-10 pr-3 py-3 rounded-lg text-[14px] focus:outline-none"
                    style={{ background: "var(--carbon)", border: "1px solid var(--steel)", color: "var(--chalk)", fontFamily: "'Inter', sans-serif" }}
                    data-testid="magic-email-input"
                  />
                </div>
                {magicError && (
                  <div className="mb-2 text-[12px]" style={{ color: "#E10600" }} data-testid="magic-error">{magicError}</div>
                )}
                <button
                  type="submit"
                  disabled={magicSending || !magicEmail.trim()}
                  className="btn w-full"
                  style={{
                    minHeight: 50,
                    background: "transparent",
                    border: "1.5px solid var(--pit-yellow)",
                    color: "var(--pit-yellow)",
                    fontFamily: "'Inter', sans-serif",
                    letterSpacing: 0,
                    textTransform: "none",
                    fontSize: "0.95rem",
                    fontWeight: 600,
                    opacity: magicSending || !magicEmail.trim() ? 0.5 : 1,
                  }}
                  data-testid="magic-link-submit"
                >
                  {magicSending ? (
                    <><Loader2 size={16} className="animate-spin" /> Envoi en cours…</>
                  ) : (
                    <><Mail size={16} /> Continuer avec mon email</>
                  )}
                </button>
              </form>
            ) : (
              <div className="mb-6 rounded-lg p-4" style={{ background: "rgba(255,196,0,0.10)", border: "1px solid rgba(255,196,0,0.35)" }} data-testid="magic-sent-success">
                <div className="flex items-start gap-3">
                  <CheckCircle2 size={20} style={{ color: "var(--pit-yellow)" }} className="shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold mb-1 text-[14px]" style={{ color: "var(--chalk)" }}>Lien envoyé !</div>
                    <div className="text-[13px] leading-relaxed" style={{ color: "var(--ink-soft)" }}>
                      Vérifiez votre boîte <strong>{magicEmail}</strong> (et le spam au cas où). Le lien est valable 15 minutes.
                    </div>
                    <button
                      type="button"
                      onClick={() => { setMagicSent(false); setMagicEmail(""); }}
                      className="text-[12px] mt-3 underline"
                      style={{ color: "var(--pit-yellow)" }}
                      data-testid="magic-resend-btn"
                    >
                      Utiliser un autre email
                    </button>
                  </div>
                </div>
              </div>
            )}

            <ul className="space-y-3 text-sm font-body">
              {[
                "Multi-tenant : votre espace, isolé et sécurisé",
                "Mobile-first : pilotez depuis le chantier",
                "Conçu pour les entrepreneurs français",
              ].map((t) => (
                <li key={t} className="flex items-start gap-3">
                  <CheckCircle2 size={16} className="mt-0.5 shrink-0" style={{ color: "var(--pit-yellow)" }} strokeWidth={2} />
                  <span style={{ color: "var(--ink-soft)" }}>{t}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="text-[11px] font-medium flex flex-wrap items-center justify-between gap-2 pt-6 border-t" style={{ borderColor: "var(--steel)", color: "var(--grey)" }}>
          <span>© 2026 PITBOARD · ENTREPRENEURS FRANÇAIS</span>
          <span>v1.2 · PIT-STOP EDITION</span>
        </div>
      </div>
    </div>
  );
}
